import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, fmtNum } from "@/lib/api";
import { Plus, Search } from "lucide-react";

export default function Quotations() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    api.get("/quotations").then((r) => setRows(r.data));
  }, []);

  const filtered = rows.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (q) {
      const hay = [
        r.quote_number,
        r.client?.company,
        r.client?.name,
        r.created_by_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <Layout>
      <PageHeader
        eyebrow="Pipeline"
        title="Quotations"
        subtitle="All rack quotations created in the system."
        actions={
          <button
            onClick={() => navigate("/configurator")}
            className="btn-primary"
            data-testid="new-quote-btn"
          >
            <Plus size={16} /> New quotation
          </button>
        }
      />

      <div className="p-8">
        <div className="panel">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-line">
            <Search size={16} className="text-ink-faint" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 outline-none bg-transparent text-sm"
              placeholder="Search quote number, client, created by…"
              data-testid="quote-search"
            />
            <div className="flex items-center gap-px border border-line bg-line">
              {["all", "draft", "sent", "approved", "rejected"].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  data-testid={`filter-${s}`}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${
                    status === s ? "bg-ink text-white" : "bg-white text-ink-muted hover:text-ink"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-16 text-center text-ink-muted">
              <div className="label-tech">No quotations</div>
            </div>
          ) : (
            <table className="w-full grid-lines" data-testid="quotations-table">
              <thead>
                <tr>
                  <th>Quote #</th>
                  <th>Client</th>
                  <th>Specs</th>
                  <th className="num">Weight</th>
                  <th className="num">Price</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/quotations/${r.id}`)}
                    className="cursor-pointer hover:bg-surfaceAlt"
                    data-testid={`quote-row-${r.quote_number}`}
                  >
                    <td className="mono font-bold">{r.quote_number}</td>
                    <td>{r.client?.company || r.client?.name || "—"}</td>
                    <td className="mono text-xs text-ink-muted">
                      {(r.items?.length || 1)} item{(r.items?.length || 1) !== 1 ? "s" : ""} · {(r.items || []).reduce((a, i) => a + (i.quantity || 1), 0) || 1} rack(s)
                    </td>
                    <td className="num">{fmtNum(r.total_weight_kg || r.result?.total_weight_kg || 0, 1)} kg</td>
                    <td className="num font-semibold">{fmtINR(r.grand_total_price || r.total_price || r.result?.total_price || 0)}</td>
                    <td>
                      <span
                        className={`inline-block px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider border ${
                          r.status === "approved"
                            ? "border-brand-success text-brand-success bg-green-50"
                            : r.status === "rejected"
                            ? "border-brand-danger text-brand-danger bg-red-50"
                            : r.status === "sent"
                            ? "border-brand-navy text-brand-navy bg-blue-50"
                            : "border-line text-ink-muted bg-surfaceAlt"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="text-xs text-ink-muted">
                      {new Date(r.created_at).toLocaleDateString("en-IN")}
                      <div className="text-[0.65rem]">{r.created_by_name}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
